import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';

interface BeforeValueCellProps {
  /** Raw pre-enrichment value for this field; `undefined` when the column has no mapping to the before-record. */
  value: unknown;
  /** Renders `value` using the same presentation as the enriched cell (e.g. `EnrichedFieldCell`). Defaults to plain text. */
  renderValue?: (value: unknown) => React.ReactNode;
  /** Automation hook: root `data-testid`; badge, value and empty state derive from it. */
  testId?: string;
}

/** Plain-text fallback for values without a caller-supplied renderer — joins arrays, stringifies scalars. */
function defaultRenderValue(value: unknown): React.ReactNode {
  if (Array.isArray(value)) return value.map((v) => String(v)).join(', ');
  return String(value);
}

/**
 * Renders one cell of the "Before" comparison row: a `Before` badge plus the
 * mapped raw feed value, or a muted "not provided in feed" message when the
 * column has no before-data mapping (`value` is `undefined`).
 */
export function BeforeValueCell({
  value,
  renderValue = defaultRenderValue,
  testId = 'before-value-cell',
}: BeforeValueCellProps) {
  const { t } = useTranslation('content-enrichment');
  const isEmpty = value == null || (Array.isArray(value) && value.length === 0);

  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <Badge
        variant="outline"
        className="text-muted-foreground w-fit"
        data-testid={`${testId}-badge`}
      >
        {t('diffTable.before.badge')}
      </Badge>
      {isEmpty ? (
        <p className="text-muted-foreground text-sm italic" data-testid={`${testId}-empty`}>
          {t('diffTable.before.notProvided')}
        </p>
      ) : (
        <div className="text-sm" data-testid={`${testId}-value`}>
          {renderValue(value)}
        </div>
      )}
    </div>
  );
}
